import React, { useContext } from "react";
import CurrencyBreakdown from "./CurrencyBreakdown";
import { FontScaleContext } from "../App";

export const CART_KEY = "frankly_cart";

export const loadCart = () => {
    try {
        return JSON.parse(sessionStorage.getItem(CART_KEY)) || [];
    } catch {
        return [];
    }
};

export const persistCart = (cart) => {
    sessionStorage.setItem(CART_KEY, JSON.stringify(cart));
};

export default function CartModal({ cart, onClose, onRemoveItem, onUpdateQty, onClearCart }) {
    const fontScale = useContext(FontScaleContext);
    const totalAvg = cart.reduce(
        (sum, item) => sum + Math.floor(Number(item.avgHabloon) || 0) * item.qty,
        0
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ background: "rgba(0,0,0,0.55)" }}
            onClick={onClose}
        >
            <div
                className="bg-gray-100 shadow-2xl w-full sm:max-w-2xl flex flex-col overflow-hidden"
                style={{ boxShadow: "0 -4px 0 0 rgba(0,0,0,0.12), 0 8px 32px rgba(0,0,0,0.25)", maxHeight: `${Math.floor(92 / fontScale)}vh` }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    className="bg-maroon px-4 py-3 flex items-center justify-between shrink-0"
                    style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.2)" }}
                >
                    <div className="flex items-center gap-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="17"
                            height="17"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="9" cy="21" r="1" />
                            <circle cx="20" cy="21" r="1" />
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                        </svg>
                        <span className="font-['Jersey_10'] text-white text-[17px] uppercase tracking-wide leading-none mt-0.5">
                            Cart
                        </span>
                        {cart.length > 0 && (
                            <span className="bg-white/20 text-white text-[11px] font-bold rounded-full px-2 py-0.5 leading-none">
                                {cart.length}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto habbo-scroll-v">
                    {cart.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="44"
                                height="44"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="9" cy="21" r="1" />
                                <circle cx="20" cy="21" r="1" />
                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                            </svg>
                            <p className="text-sm font-bold">Your cart is empty</p>
                            <p className="text-xs text-gray-400">Add furni from the values list to compare totals</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200/80">
                            {/* Column headers */}
                            <div className="hidden sm:grid grid-cols-[40px_1fr_auto_80px_32px] gap-3 items-center px-4 py-2 bg-gray-200/60">
                                <div />
                                <div className="text-[10px] font-bold tracking-wide uppercase text-gray-500">Item</div>
                                <div className="text-[10px] font-bold tracking-wide uppercase text-gray-500">Qty</div>
                                <div className="text-[10px] font-bold tracking-wide uppercase text-gray-500 text-right">Total (avg)</div>
                                <div />
                            </div>

                            {cart.map((item) => {
                                const itemTotal = Math.floor(Number(item.avgHabloon) || 0) * item.qty;
                                return (
                                    <div
                                        key={item.id}
                                        className="flex flex-col sm:grid sm:grid-cols-[40px_1fr_auto_80px_32px] gap-2 sm:gap-3 items-start sm:items-center px-4 py-3 hover:bg-white/40 transition-colors"
                                    >
                                        {/* Icon */}
                                        <div className="hidden sm:flex w-10 h-10 shrink-0 bg-white/60 rounded border border-black/5 items-center justify-center overflow-hidden">
                                            {item.iconImg && (
                                                <img
                                                    src={item.iconImg}
                                                    alt={item.name}
                                                    draggable={false}
                                                    style={{ imageRendering: "pixelated", maxWidth: "100%", maxHeight: "100%" }}
                                                />
                                            )}
                                        </div>

                                        {/* Name + breakdown */}
                                        <div className="flex-1 min-w-0 w-full">
                                            <div className="flex items-start gap-2">
                                                {/* Mobile icon */}
                                                <div className="sm:hidden w-8 h-8 shrink-0 bg-white/60 rounded border border-black/5 flex items-center justify-center overflow-hidden">
                                                    {item.iconImg && (
                                                        <img
                                                            src={item.iconImg}
                                                            alt={item.name}
                                                            draggable={false}
                                                            style={{ imageRendering: "pixelated", maxWidth: "100%", maxHeight: "100%" }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-bold text-gray-900 truncate">{item.name}</div>
                                                    <div className="mt-0.5">
                                                        <CurrencyBreakdown value={itemTotal} size="sm" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Qty controls */}
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button
                                                onClick={() => onUpdateQty(item.id, Math.max(1, item.qty - 1))}
                                                className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 active:bg-gray-400 flex items-center justify-center text-gray-700 font-bold text-base leading-none transition-colors"
                                                style={{ boxShadow: "0 2px 0 rgba(0,0,0,0.1)" }}
                                            >
                                                −
                                            </button>
                                            <span className="text-sm font-bold text-gray-900 w-6 text-center tabular-nums">
                                                {item.qty}
                                            </span>
                                            <button
                                                onClick={() => onUpdateQty(item.id, item.qty + 1)}
                                                className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 active:bg-gray-400 flex items-center justify-center text-gray-700 font-bold text-base leading-none transition-colors"
                                                style={{ boxShadow: "0 2px 0 rgba(0,0,0,0.1)" }}
                                            >
                                                +
                                            </button>
                                        </div>

                                        {/* Total */}
                                        <div className="text-sm font-bold text-gray-900 shrink-0 sm:text-right tabular-nums">
                                            {itemTotal}H
                                        </div>

                                        {/* Remove */}
                                        <button
                                            onClick={() => onRemoveItem(item.id)}
                                            className="shrink-0 text-gray-300 hover:text-red-500 active:text-red-600 transition-colors self-start sm:self-auto"
                                            title="Remove"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="15"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                            </svg>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {cart.length > 0 && (
                    <div
                        className="border-t border-black/10 px-4 py-3 bg-white/50 shrink-0 space-y-2"
                        style={{ boxShadow: "0 -2px 0 0 rgba(0,0,0,0.05)" }}
                    >
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="text-[10px] tracking-wide font-bold text-gray-500 uppercase shrink-0">
                                Grand Total (avg)
                            </div>
                            <div className="flex items-center gap-2 flex-wrap flex-1 justify-end">
                                <CurrencyBreakdown value={totalAvg} size="md" />
                                <span className="text-sm font-bold text-gray-900 tabular-nums">{totalAvg}H</span>
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={onClearCart}
                                className="px-3 py-1.5 text-[11px] font-bold text-red-500 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded border border-red-200/80 transition-colors"
                                style={{ boxShadow: "0 2px 0 rgba(239,68,68,0.15)" }}
                            >
                                Clear Cart
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}